import * as THREE from "three/webgpu";
import { triangulateTopologyForRendering } from "../modeling/topology.ts";
import type { PolygonTopology, SceneMaterial } from "../modeling/types.ts";

export interface OBJSerializationOptions {
  topology?: PolygonTopology;
  materialNamesByIndex?: Array<string | undefined>;
}

export interface OBJSerializationResult {
  text: string;
  vCount: number;
  vtCount: number;
  vnCount: number;
}

export interface MeshExportVisibilityObject {
  id: string;
  name: string;
}

export interface MeshExportVisibilityState {
  visibility: ReadonlyMap<string, boolean>;
  sceneObjects: ReadonlyMap<string, MeshExportVisibilityObject>;
}

interface GeometryGroupRange {
  start: number;
  count: number;
  materialIndex?: number;
}

/** Keep OBJ identifiers single-line and non-empty. */
export function sanitizeOBJName(raw: string, fallback: string): string {
  const cleaned = raw
    .replace(/[\r\n\t]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
  return cleaned || fallback;
}

/** Keep download filenames compact, portable, and single-line. */
export function sanitizeExportFilenameStem(
  raw: string,
  fallback: string,
): string {
  const cleaned = raw
    // biome-ignore lint/suspicious/noControlCharactersInRegex: intentional control-char strip for filesystem-safe filenames
    .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "_")
    .replace(/[\r\n\t]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
  return cleaned || fallback;
}

/**
 * Build a unique basename for multi-file exports so sidecar references like
 * `mtllib` keep matching the actual downloaded filenames even when the browser
 * would otherwise auto-rename one of the files due to an existing collision.
 */
export function buildPairedExportStem(
  baseName: string,
  now: Date = new Date(),
  randomToken: string = Math.random().toString(36).slice(2, 6),
): string {
  const safeBase = sanitizeExportFilenameStem(baseName, "export");
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const h = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  const s = String(now.getSeconds()).padStart(2, "0");
  const token = sanitizeExportFilenameStem(randomToken, "pair").replace(
    /\s+/g,
    "_",
  );
  return `${safeBase}_${y}${mo}${d}_${h}${mi}${s}_${token}`;
}

export function isMeshExportSourceVisible(
  state: MeshExportVisibilityState,
  key: string,
): boolean {
  const obj = state.sceneObjects.get(key);
  const keys = obj ? [key, obj.id, obj.name] : [key];
  for (const candidate of keys) {
    const visible = state.visibility.get(candidate);
    if (visible !== undefined) return visible;
  }
  return true;
}

export function filterVisibleMeshExportSourceKeys(
  keys: readonly string[],
  state: MeshExportVisibilityState,
): string[] {
  return keys.filter((key) => isMeshExportSourceVisible(state, key));
}

/** Collect the material indices referenced by the final geometry groups. */
export function collectUsedMaterialIndices(
  geo: THREE.BufferGeometry,
): Set<number> {
  const used = new Set<number>();
  if (geo.groups.length === 0) {
    used.add(0);
    return used;
  }
  for (const group of geo.groups) {
    used.add(group.materialIndex ?? 0);
  }
  return used;
}

/**
 * Serialize one object block for an OBJ file. Returns the text and the
 * number of vertices/UVs/normals written (for offset tracking in
 * multi-object exports). `offsets` are the running totals of v/vt/vn
 * written by preceding objects (OBJ indices are global and 1-based).
 */
export function serializeOBJBlock(
  geo: THREE.BufferGeometry,
  name: string,
  offsets: { v: number; vt: number; vn: number },
  options: OBJSerializationOptions = {},
): OBJSerializationResult {
  const lines: string[] = [];
  const safeName = sanitizeOBJName(name, "Object");
  lines.push(`o ${safeName}`);

  const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
  const normalAttr = geo.getAttribute("normal") as
    | THREE.BufferAttribute
    | undefined;
  const uvAttr = geo.getAttribute("uv") as THREE.BufferAttribute | undefined;
  const indexAttr = geo.getIndex();
  const vCount = posAttr.count;

  for (let i = 0; i < vCount; i++) {
    lines.push(`v ${posAttr.getX(i)} ${posAttr.getY(i)} ${posAttr.getZ(i)}`);
  }

  const vtCount = uvAttr ? uvAttr.count : 0;
  if (uvAttr) {
    for (let i = 0; i < vtCount; i++) {
      lines.push(`vt ${uvAttr.getX(i)} ${uvAttr.getY(i)}`);
    }
  }

  const vnCount = normalAttr ? normalAttr.count : 0;
  if (normalAttr) {
    for (let i = 0; i < vnCount; i++) {
      lines.push(
        `vn ${normalAttr.getX(i)} ${normalAttr.getY(i)} ${normalAttr.getZ(i)}`,
      );
    }
  }

  lines.push(`g ${safeName}`);

  const materialNamesByIndex = options.materialNamesByIndex;
  const activeMaterial = { current: "" };
  const emitMaterial = (materialIndex: number): void => {
    if (!materialNamesByIndex || materialNamesByIndex.length === 0) return;
    const next = materialNamesByIndex[materialIndex] ?? materialNamesByIndex[0];
    if (!next || next === activeMaterial.current) return;
    activeMaterial.current = next;
    lines.push(`usemtl ${next}`);
  };

  const elementCount = indexAttr ? indexAttr.count : vCount;
  const triMaterialIndices = buildTriangleMaterialIndices(
    elementCount,
    geo.groups as unknown as GeometryGroupRange[],
  );
  const topology = options.topology;
  const faceMaterialIndices =
    topology && canUseTopologyForOBJ(geo, topology)
      ? buildFaceMaterialIndices(topology, triMaterialIndices)
      : null;

  if (topology && faceMaterialIndices) {
    const faceCount = topology.offsets.length - 1;
    for (let f = 0; f < faceCount; f++) {
      emitMaterial(faceMaterialIndices[f]);
      const start = topology.offsets[f];
      const end = topology.offsets[f + 1];
      const parts: string[] = [];
      for (let c = start; c < end; c++) {
        const vi = c + 1 + offsets.v;
        const vti = uvAttr ? c + 1 + offsets.vt : 0;
        const vni = normalAttr ? c + 1 + offsets.vn : 0;
        if (normalAttr && uvAttr) parts.push(`${vi}/${vti}/${vni}`);
        else if (uvAttr) parts.push(`${vi}/${vti}`);
        else if (normalAttr) parts.push(`${vi}//${vni}`);
        else parts.push(`${vi}`);
      }
      lines.push(`f ${parts.join(" ")}`);
    }
  } else {
    const triCount = Math.floor(elementCount / 3);
    for (let tri = 0; tri < triCount; tri++) {
      emitMaterial(triMaterialIndices ? triMaterialIndices[tri] : 0);
      const parts: string[] = [];
      for (let j = 0; j < 3; j++) {
        const srcIndex = indexAttr ? indexAttr.getX(tri * 3 + j) : tri * 3 + j;
        const vi = srcIndex + 1 + offsets.v;
        const vti = uvAttr ? srcIndex + 1 + offsets.vt : 0;
        const vni = normalAttr ? srcIndex + 1 + offsets.vn : 0;
        if (normalAttr && uvAttr) parts.push(`${vi}/${vti}/${vni}`);
        else if (uvAttr) parts.push(`${vi}/${vti}`);
        else if (normalAttr) parts.push(`${vi}//${vni}`);
        else parts.push(`${vi}`);
      }
      lines.push(`f ${parts.join(" ")}`);
    }
  }

  return { text: lines.join("\n") + "\n", vCount, vtCount, vnCount };
}

/** Serialize a SceneMaterial to Wavefront MTL format. */
export function serializeMTL(materialName: string, mat: SceneMaterial): string {
  const lines: string[] = [];
  lines.push(`# PoseRef MTL Export`);
  lines.push(`newmtl ${materialName}`);

  const c = new THREE.Color(mat.color);
  lines.push(`Kd ${c.r.toFixed(6)} ${c.g.toFixed(6)} ${c.b.toFixed(6)}`);

  const specular =
    mat.metalness > 0
      ? `${(c.r * mat.metalness).toFixed(6)} ${(c.g * mat.metalness).toFixed(6)} ${(c.b * mat.metalness).toFixed(6)}`
      : "0.040000 0.040000 0.040000";
  lines.push(`Ks ${specular}`);

  const ns = Math.max(1, (1 - mat.roughness) * 250);
  lines.push(`Ns ${ns.toFixed(4)}`);

  const opacity = mat.opacity ?? 1.0;
  lines.push(`d ${opacity.toFixed(4)}`);
  lines.push(`illum 2`);

  if (
    mat.emissiveColor &&
    mat.emissiveColor !== "#000000" &&
    (mat.emissiveIntensity ?? 0) > 0
  ) {
    const e = new THREE.Color(mat.emissiveColor);
    const ei = mat.emissiveIntensity ?? 1;
    lines.push(
      `Ke ${(e.r * ei).toFixed(6)} ${(e.g * ei).toFixed(6)} ${(e.b * ei).toFixed(6)}`,
    );
  }

  return lines.join("\n") + "\n";
}

function canUseTopologyForOBJ(
  geo: THREE.BufferGeometry,
  topology: PolygonTopology,
): boolean {
  const posAttr = geo.getAttribute("position") as
    | THREE.BufferAttribute
    | undefined;
  const indexAttr = geo.getIndex();
  if (!posAttr || !indexAttr) return false;
  if (posAttr.count !== topology.corners.length) return false;
  return indexAttr.count === triangulateTopologyForRendering(topology).length;
}

function buildTriangleMaterialIndices(
  elementCount: number,
  groups: GeometryGroupRange[],
): Uint16Array | null {
  const triCount = Math.floor(elementCount / 3);
  if (triCount === 0) return null;

  const out = new Uint16Array(triCount);
  if (groups.length === 0) return out;

  for (const group of groups) {
    const startTri = Math.max(0, Math.floor(group.start / 3));
    const endTri = Math.min(
      triCount,
      Math.ceil((group.start + group.count) / 3),
    );
    const materialIndex = group.materialIndex ?? 0;
    for (let tri = startTri; tri < endTri; tri++) out[tri] = materialIndex;
  }
  return out;
}

function buildFaceMaterialIndices(
  topology: PolygonTopology,
  triMaterialIndices: Uint16Array | null,
): Uint16Array | null {
  const faceCount = topology.offsets.length - 1;
  if (faceCount === 0) return null;
  const out = new Uint16Array(faceCount);
  let triCursor = 0;

  for (let f = 0; f < faceCount; f++) {
    const size = topology.offsets[f + 1] - topology.offsets[f];
    const triCount = Math.max(0, size - 2);
    const faceMaterial =
      triCount > 0 && triMaterialIndices ? triMaterialIndices[triCursor] : 0;
    for (let t = 1; t < triCount; t++) {
      if (
        !triMaterialIndices ||
        triMaterialIndices[triCursor + t] !== faceMaterial
      ) {
        return null;
      }
    }
    out[f] = faceMaterial;
    triCursor += triCount;
  }

  return out;
}
